import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  OnInit,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { formatDate } from '@angular/common';
import { Orders } from 'src/app/models/Orders.model';
import { Documents } from 'src/app/models/documents.models';
import { OrdersService } from 'src/app/services/orders.service';
import { OpenModalService } from 'src/app/services/shared/open-modal.service';

/**
 * BorderòDettagliComponent — Diakonix modal opened from BorderòComponent
 * (list of generated borderò).
 *
 * Shows the order info attached to the borderò (number, dates,
 * customer, shipping address, carrier) + three collapsible sections:
 *   • Prodotti     — list of products in the order
 *   • Documenti    — DDT / certificates / other docs
 *   • Etichette    — PDF links (borderò, all-box-labels, carrier)
 *
 * Phase 1.5 / v2.7.0 — TS cleaned up (Chunk C.2):
 *   • dropped 4 unused @Input copy-paste leftovers (`code`,
 *     `isProdOpen`, `product_id` — modal template noise),
 *   • dropped 4 kendo-dialog sizing leftovers (`dialogWidth`,
 *     `dialogHeight`, `maxlength`, `minWidth`),
 *   • dropped unused state: `goBack`, `dataSaved`, `isEditable`,
 *     `clickedProduct`, `clickLabel`, `ettArray`, `linkCarrier`
 *     (kept linkBordero + linkAllLabels),
 *   • dropped `goEditFunction`, `productDetails`, `goToProductDetails`,
 *     `triggerFunction`, `printBordero` (replaced with the simpler
 *     `<a target="_blank">` in the new HTML),
 *   • dropped ~5 console.log,
 *   • switch to inject() + takeUntilDestroyed.
 *
 * Behaviour preserved 1:1: loadOrder by order_id, loadDocuments,
 * printAllLabels (still kicked off on load), close-flow via
 * closeServ.offStorico().
 */
@Component({
  selector: 'app-borderò-dettagli',
  templateUrl: './borderò-dettagli.component.html',
  styleUrls: ['./borderò-dettagli.component.scss'],
  standalone: false,
})


export class BorderòDettagliComponent implements OnInit {

  private readonly ordersServ = inject(OrdersService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  public readonly closeServ = inject(OpenModalService);

  @Input() opened = false;
  @Input() order_id = '-1';

  // ── Data ────────────────────────────────────────────────────────
  public order: Orders.IOrder | null = null;
  public documents: Documents.Document[] = [];

  // ── PDF links surfaced in the "Etichette" section ───────────────
  public linkBordero: string | null = null;
  public linkAllLabels: string | null = null;
  public linkToDDT: string | null = null;
  public isLinkToDDT = false;

  // ── UI state ────────────────────────────────────────────────────
  public loading = false;
  public formattedDate = '—';

  /** Which accordion section is currently open. Three are mutually
   *  exclusive (open one → close the others). Open 'products' by
   *  default — most-requested first. */
  public openSection: 'products' | 'documents' | 'labels' | null = 'products';

  // ──────────────────────────────────────────────────────────────────
  // Lifecycle.
  // ──────────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.loadOrder();
  }

  private loadOrder(): void {
    this.loading = true;
    this.ordersServ
      .getOrder1(+this.order_id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (orderData) => {
          if (!orderData) {
            this.loading = false;
            this.cdr.detectChanges();
            return;
          }
          this.order = orderData;
          this.linkBordero = orderData.or_url_box_label ?? null;
          this.isLinkToDDT = !!orderData.or_url_ddt?.includes('.pdf');
          this.linkToDDT = this.isLinkToDDT ? orderData.or_url_ddt : null;

          if (orderData.or_assignment_date) {
            this.formattedDate = formatDate(
              orderData.or_assignment_date,
              'dd/MM/yyyy',
              'it',
            );
          }

          this.loadDocuments();
          this.printAllLabels();
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.loading = false;
          this.cdr.detectChanges();
        },
      });
  }

  private loadDocuments(): void {
    this.ordersServ
      .getDocuments(+this.order_id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.documents = result ?? [];
          // The legacy code also called `getOrderDocuments(order_id)`
          // and overwrote `documents` if it returned anything. Same
          // behaviour preserved here.
          this.ordersServ
            .getOrderDocuments(this.order_id)
            .then((res) => {
              if (res) this.documents = res;
              this.cdr.detectChanges();
            });
        },
      });
  }

  private printAllLabels(): void {
    if (!this.order?.order_id || !this.order?.boxes?.length) return;
    this.ordersServ
      .getAllBoxLabels(
        this.order.order_id,
        this.order.boxes.map((_, i) => i + 1),
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          if (!result) return;
          this.linkAllLabels = result.box_label_link_pdf ?? null;
          this.cdr.detectChanges();
        },
      });
  }

  // ──────────────────────────────────────────────────────────────────
  // Section toggle (single-open accordion).
  // ──────────────────────────────────────────────────────────────────

  toggleSection(name: 'products' | 'documents' | 'labels'): void {
    this.openSection = this.openSection === name ? null : name;
  }

  isOpen(name: 'products' | 'documents' | 'labels'): boolean {
    return this.openSection === name;
  }

  // ──────────────────────────────────────────────────────────────────
  // Close.
  // ──────────────────────────────────────────────────────────────────

  close(): void {
    this.closeServ.offStorico();
  }

  goBack(): void {
    this.close();
  }
  
}
